var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faMaximize, faMinimize } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, Stack, Tooltip, styled } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanType, PlanTypeCategory } from '../../enum';
import { Autocomplete, ErrorMessageBox, Table, TableCell, TableContainer, TableHead, TableRow } from '../common';
import { ADJUSTED_COLOR } from '../daily/constants';
import { useReportTimeline } from '../daily/selectors';
import { QueueType } from '../multiskill/types';
import { useExportAPI, useForbiddenRedirect, useReportAPI } from '../report/selectors';
import { SeatExceededIcon, periodIndexToYearMonth, useSeatCapacity } from '../seatCapacity';
import { TimelineType } from '../wizard/selectors';
import { AgentRequirementsTable } from './agentRequirements';
import { FinancialPeriodChart, ResourcingPeriodChart } from './chart';
import { AgentSalariesTable, CostPerWorkTable, HiringAheadOfAttritionTable, OvertimeCostsTable, StaffBudgetTable, SupportStaffSalariesTable } from './financialTable';
import { ProdHoursPeriodTable } from './prodHours';
import { SeatsRequiredTable } from './seatsRequired';
import { usePeriodReportAPI, usePeriodReportByMode, usePeriodReportChunkSize, usePrintPeriodTableAPI } from './selectors';
import { SupportStaffTable } from './supportStaff';
import { WorkloadAndPerformanceTable } from './workloadAndPerformance';
var STICKIES = ['10.75rem'];
var DEFAULT_ADD_COLUMNS = ['Average'];
export var StyledTableCell = styled(TableCell)(function (_a) {
  var theme = _a.theme;
  return {
    minWidth: theme.typography.pxToRem(100),
    whiteSpace: 'nowrap'
  };
});
export var StyledFirstCell = styled(TableCell)(function (_a) {
  var theme = _a.theme,
    paddingLeft = _a["aria-label"];
  return {
    textAlign: 'left',
    minWidth: theme.typography.pxToRem(400),
    '&.MuiTableCell-root': {
      paddingLeft: paddingLeft,
      borderLeft: "1px solid ".concat(theme.palette.grey[600])
    }
  };
});
export var PeriodTableHead = function (_a) {
  var _b;
  var _c = _a.headColor,
    headColor = _c === void 0 ? ADJUSTED_COLOR : _c,
    _d = _a.addColumns,
    addColumns = _d === void 0 ? DEFAULT_ADD_COLUMNS : _d,
    dataType = _a.dataType,
    _e = _a.firstHeader,
    firstHeader = _e === void 0 ? false : _e,
    expanded = _a.expanded,
    isExpandable = _a.isExpandable,
    onExpandToggle = _a.onExpandToggle,
    scenarioSelector = _a.scenarioSelector,
    summaryColumnLabel = _a.summaryColumnLabel,
    planId = _a.planId,
    props = __rest(_a, ["headColor", "addColumns", "dataType", "firstHeader", "expanded", "isExpandable", "onExpandToggle", "scenarioSelector", "summaryColumnLabel", "planId"]);
  var t = useTranslation(['report', 'translation']).t;
  var reportData = useReportAPI()[0].data;
  var timelines = useReportTimeline.apply(void 0, __spreadArray([!props.index, !props.index, true], addColumns, false));
  var _f = reportData !== null && reportData !== void 0 ? reportData : {},
    planType = _f.planType,
    startDate = _f.startDate;
  var isMonthly = planType === PlanType.Monthly;
  var isIncludeTotal = addColumns.includes('Total');
  var displayName = props.namePlan + (dataType === QueueType.Multiskill ? " ".concat(t('report:multiskill')) : '');
  var chunkSize = usePeriodReportChunkSize();
  var _g = useSeatCapacity(),
    isScenarioMonthExceeded = _g.isScenarioMonthExceeded,
    openAlertDetails = _g.openAlertDetails;
  /**
   * Map a header column position back to its 0-based period index. Weekly/MonthlyCustom rows are
   * flat (`[...periods, ...addColumns]`); standard monthly rows are chunked into calendar years
   * (`[...12 months, ...addColumns]` repeated), so trailing Total/Average columns must be skipped
   * per chunk, not just once at the end.
   */
  var getHeaderPeriodIndex = function (type, cellIdx, arrLength) {
    if (type === TimelineType.Years) return undefined;
    var addColumnsLength = addColumns.length;
    if (isMonthly && type !== TimelineType.Weeks) {
      var chunkGroupSize = chunkSize + addColumnsLength;
      var positionInChunk = cellIdx % chunkGroupSize;
      if (positionInChunk >= chunkSize) return undefined;
      return Math.floor(cellIdx / chunkGroupSize) * chunkSize + positionInChunk;
    }
    return cellIdx < arrLength - addColumnsLength ? cellIdx : undefined;
  };
  var isHeaderPeriodExceeded = function (type, cellIdx, arrLength) {
    if (planId == null || !startDate) return false;
    var periodIndex = getHeaderPeriodIndex(type, cellIdx, arrLength);
    if (periodIndex == null) return false;
    var _a = periodIndexToYearMonth(startDate, planType !== null && planType !== void 0 ? planType : PlanType.Monthly, periodIndex),
      year = _a.year,
      month = _a.month;
    return isScenarioMonthExceeded(planId, year, month);
  };
  // Weekly / MonthlyCustom headers have a Week/Month-N row plus a date sub-row; show the icon only
  // on the first period row (not duplicated on Dates). Standard Monthly has only the Dates row.
  var seatExceededHeaderType = (_b = timelines.find(function (_a) {
    var type = _a[1];
    return type !== TimelineType.Years;
  })) === null || _b === void 0 ? void 0 : _b[1];
  var renderFirstCellContent = function (cellValue) {
    if (!cellValue) return null;
    var isTitleCell = cellValue === props.title;
    if (isTitleCell && isExpandable) {
      return _jsxs(Stack, __assign({
        direction: "row",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%"
      }, {
        children: [_jsx("span", {
          children: cellValue
        }), _jsx(Tooltip, __assign({
          title: expanded ? t('report:period.collapse') : t('report:period.expand'),
          placement: "top"
        }, {
          children: _jsx(Link, __assign({
            component: "button",
            onClick: function (e) {
              e.stopPropagation();
              onExpandToggle === null || onExpandToggle === void 0 ? void 0 : onExpandToggle();
            },
            sx: {
              fontWeight: 'normal',
              color: 'white',
              ':hover': {
                color: 'white'
              }
            }
          }, {
            children: _jsx(FontAwesomeIcon, {
              icon: expanded ? faMinimize : faMaximize
            })
          }))
        }))]
      }));
    }
    return cellValue;
  };
  var getColSpan = function (title, type) {
    if (type === TimelineType.Years) {
      return 14;
    }
    return ['Average', ''].includes(title) && !isIncludeTotal ? 2 : 1;
  };
  var getValue = function (title, type, index, arr) {
    var singleSummaryColumnLabel = summaryColumnLabel !== null && summaryColumnLabel !== void 0 ? summaryColumnLabel : t('translation:table.totalAverage', {
      defaultValue: 'Total / Average'
    });
    if (type === TimelineType.Years) {
      return title;
    }
    if (type === TimelineType.Weeks && ['Average', 'Total'].includes(title)) {
      return '';
    }
    if (title === 'Average' && !isIncludeTotal) {
      return singleSummaryColumnLabel;
    }
    if (index === arr - 1) {
      if (!isIncludeTotal) return singleSummaryColumnLabel;
      return summaryColumnLabel !== null && summaryColumnLabel !== void 0 ? summaryColumnLabel : t('translation:table.average');
    }
    if (index === arr - 2 && isIncludeTotal) {
      return t('translation:table.total');
    }
    return title;
  };
  var getHeaderCellColor = function (type, rowIdx) {
    if (type === TimelineType.Weeks && !rowIdx) {
      return headColor;
    }
    if (isMonthly) {
      return headColor;
    }
    return undefined;
  };
  return _jsxs(TableHead, {
    children: [timelines.map(function (_a, rowIdx) {
      var titles = _a[0],
        type = _a[1];
      return _jsx(React.Fragment, {
        children: firstHeader ? _jsxs(TableRow, {
          children: [rowIdx === 0 ? _jsx(_Fragment, {
            children: type === TimelineType.Weeks ? _jsx(StyledFirstCell, __assign({
              stickySizes: STICKIES,
              color: headColor
            }, {
              children: scenarioSelector !== null && scenarioSelector !== void 0 ? scenarioSelector : displayName
            })) : _jsx(StyledFirstCell, __assign({
              rowSpan: timelines.length,
              color: headColor,
              stickySizes: STICKIES
            }, {
              children: scenarioSelector !== null && scenarioSelector !== void 0 ? scenarioSelector : displayName
            }))
          }) : function () {
            if (isMonthly) {
              return null;
            }
            return _jsx(StyledFirstCell, __assign({
              stickySizes: STICKIES
            }, {
              children: renderFirstCellContent(props.title)
            }));
          }(), titles === null || titles === void 0 ? void 0 : titles.map(function (title, cellIdx, arr) {
            var displayValue = getValue(title, type, cellIdx, arr.length);
            var exceeded = type === seatExceededHeaderType && isHeaderPeriodExceeded(type, cellIdx, arr.length);
            return _jsx(StyledTableCell, __assign({
              color: getHeaderCellColor(type, rowIdx),
              colSpan: getColSpan(title, type),
              highlightBorder: cellIdx !== arr.length - 1 && (type === TimelineType.Years || ['Average'].includes(title)),
              value: exceeded ? undefined : displayValue
            }, {
              children: exceeded && _jsxs(Stack, __assign({
                direction: "row",
                alignItems: "center",
                justifyContent: "center",
                spacing: 0.5
              }, {
                children: [_jsx("span", {
                  children: displayValue
                }), _jsx(SeatExceededIcon, {
                  onClick: openAlertDetails
                })]
              }))
            }), "".concat(type, "-").concat(cellIdx, "-").concat(title));
          })]
        }) : !rowIdx && !isMonthly && _jsxs(TableRow, {
          children: [_jsx(StyledFirstCell, __assign({
            stickySizes: STICKIES
          }, {
            children: renderFirstCellContent(props.title)
          })), titles === null || titles === void 0 ? void 0 : titles.map(function (title, cellIdx, arr) {
            return _jsx(StyledTableCell, {
              color: type === TimelineType.Weeks ? undefined : headColor,
              colSpan: getColSpan(title, type),
              highlightBorder: cellIdx !== arr.length - 1 && (type === TimelineType.Years || ['Average'].includes(title)),
              value: ''
            }, "".concat(type, "-").concat(cellIdx, "-").concat(title));
          })]
        })
      }, "".concat(type, "-").concat(rowIdx));
    }), props.title && isMonthly && _jsxs(TableRow, {
      children: [_jsx(StyledFirstCell, __assign({
        stickySizes: STICKIES
      }, {
        children: renderFirstCellContent(props.title)
      })), function () {
        var monthlyTimeline = timelines.find(function (_a) {
          var type = _a[1];
          return type !== TimelineType.Years;
        });
        if (!monthlyTimeline) return null;
        var titles = monthlyTimeline[0],
          type = monthlyTimeline[1];
        return titles === null || titles === void 0 ? void 0 : titles.map(function (title, cellIdx, arr) {
          return _jsx(StyledTableCell, {
            colSpan: getColSpan(title, type),
            highlightBorder: cellIdx !== arr.length - 1 && (type === TimelineType.Years || ['Average'].includes(title)),
            value: ''
          }, "".concat(type, "-").concat(cellIdx, "-").concat(title));
        });
      }()]
    })]
  });
};
var PeriodTable = function (props) {
  var periodData = usePeriodReportAPI()[0];
  if (!periodData.data) return null;
  return _jsx(TableContainer, __assign({
    topScroll: true,
    sx: {
      border: 'unset'
    }
  }, {
    children: _jsx(Table, {
      children: props.children
    })
  }));
};
// Helper function to determine if a plan should only show Average column (no Total)
// Both ProdHours and WorkVolume plans should only show Average
var shouldShowOnlyAverage = function (planTypeCategory) {
  return planTypeCategory === PlanTypeCategory.ProdHours || planTypeCategory === PlanTypeCategory.WorkVolume;
};
var getScenarioOptionId = function (plan, index) {
  return "".concat(plan.displayName, "-").concat(plan.dataType, "-").concat(index);
};
var getScenarioOptionLabel = function (plan, t) {
  return plan.displayName + (plan.dataType === QueueType.Multiskill ? " ".concat(t('multiskill')) : '');
};
var useScenarioSelection = function (plans, t) {
  var scenarioOptions = useMemo(function () {
    return (plans !== null && plans !== void 0 ? plans : []).map(function (plan, index) {
      return {
        id: getScenarioOptionId(plan, index),
        name: getScenarioOptionLabel(plan, t),
        plan: plan
      };
    });
  }, [plans, t]);
  var _a = useState(''),
    selectedScenarioId = _a[0],
    setSelectedScenarioId = _a[1];
  useEffect(function () {
    if (!scenarioOptions.length) {
      setSelectedScenarioId('');
      return;
    }
    setSelectedScenarioId(function (prev) {
      return scenarioOptions.some(function (option) {
        return option.id === prev;
      }) ? prev : scenarioOptions[0].id;
    });
  }, [scenarioOptions]);
  var selectedPlans = useMemo(function () {
    if (!selectedScenarioId) return [];
    return scenarioOptions.filter(function (option) {
      return option.id === selectedScenarioId;
    }).map(function (option) {
      return option.plan;
    });
  }, [scenarioOptions, selectedScenarioId]);
  return {
    scenarioOptions: scenarioOptions,
    selectedScenarioId: selectedScenarioId,
    selectedPlans: selectedPlans,
    setSelectedScenarioId: setSelectedScenarioId
  };
};
export var ResourcingTable = function () {
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var periodData = usePeriodReportAPI()[0];
  var printData = usePrintPeriodTableAPI()[0];
  var exportData = useExportAPI()[0];
  var periodDataMode = usePeriodReportByMode();
  var reportData = useReportAPI()[0].data;
  var isProdHoursPlan = (reportData === null || reportData === void 0 ? void 0 : reportData.planTypeCategory) === PlanTypeCategory.ProdHours;
  var _a = useScenarioSelection(periodDataMode, t),
    scenarioOptions = _a.scenarioOptions,
    selectedScenarioId = _a.selectedScenarioId,
    selectedPlans = _a.selectedPlans,
    setSelectedScenarioId = _a.setSelectedScenarioId;
  // For prod hours and volume-based plans, only show Average column (no Total)
  var addColumns = shouldShowOnlyAverage(reportData === null || reportData === void 0 ? void 0 : reportData.planTypeCategory) ? ['Average'] : ['Total', 'Average'];
  return _jsxs(_Fragment, {
    children: [_jsx(ErrorMessageBox, {
      errors: [periodData.error, printData.error, exportData.error]
    }), _jsx(ResourcingPeriodChart, {}), _jsx(PeriodTable, {
      children: selectedPlans.map(function (plan, index) {
        var scenarioSelector = !!scenarioOptions.length && _jsx(Autocomplete, {
          size: "small",
          sx: {
            maxWidth: '17rem',
            bgcolor: 'background.paper'
          },
          value: selectedScenarioId,
          options: scenarioOptions,
          getOptionValue: function (option) {
            return option.id;
          },
          onChange: setSelectedScenarioId
        });
        if (isProdHoursPlan) {
          return _jsx(ProdHoursPeriodTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            scenarioSelector: scenarioSelector
          }, "".concat(plan.displayName, "-").concat(plan.dataType, "-").concat(index));
        }
        return _jsxs(React.Fragment, {
          children: [_jsx(WorkloadAndPerformanceTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: isProdHoursPlan,
            scenarioSelector: scenarioSelector
          }), _jsx(AgentRequirementsTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: isProdHoursPlan
          }), _jsx(SeatsRequiredTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: isProdHoursPlan
          }), _jsx(SupportStaffTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: isProdHoursPlan
          })]
        }, "".concat(plan.displayName, "-").concat(plan.dataType, "-").concat(index));
      })
    })]
  });
};
export var FinancialTable = function () {
  var t = useTranslation('report', {
    keyPrefix: 'period'
  }).t;
  var periodData = usePeriodReportAPI()[0];
  var printData = usePrintPeriodTableAPI()[0];
  var exportData = useExportAPI()[0];
  var periodDataMode = usePeriodReportByMode();
  useForbiddenRedirect([printData.statusCode, exportData.statusCode]);
  var _a = useScenarioSelection(periodDataMode, t),
    scenarioOptions = _a.scenarioOptions,
    selectedScenarioId = _a.selectedScenarioId,
    selectedPlans = _a.selectedPlans,
    setSelectedScenarioId = _a.setSelectedScenarioId;
  // Financial rows are currency amounts (budgets/salaries/costs), so a period Total is meaningful for both weekly
  // and monthly plans. Show Total alongside Average on every financial section, matching the Resourcing view. (MUL-79)
  var addColumns = ['Total', 'Average'];
  return _jsxs(_Fragment, {
    children: [_jsx(ErrorMessageBox, {
      errors: [periodData.error, printData.error, exportData.error]
    }), _jsx(FinancialPeriodChart, {}), _jsx(PeriodTable, {
      children: selectedPlans === null || selectedPlans === void 0 ? void 0 : selectedPlans.map(function (plan, index) {
        return _jsxs(React.Fragment, {
          children: [_jsx(StaffBudgetTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: false,
            scenarioSelector: !!scenarioOptions.length && _jsx(Autocomplete, {
              size: "small",
              sx: {
                maxWidth: '17rem',
                bgcolor: 'background.paper'
              },
              value: selectedScenarioId,
              options: scenarioOptions,
              getOptionValue: function (option) {
                return option.id;
              },
              onChange: setSelectedScenarioId
            })
          }), _jsx(AgentSalariesTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: false
          }), _jsx(SupportStaffSalariesTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: false
          }), _jsx(HiringAheadOfAttritionTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: false
          }), _jsx(OvertimeCostsTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: false
          }), _jsx(CostPerWorkTable, {
            data: plan,
            index: index,
            addColumns: addColumns,
            isProdHoursPlan: false
          })]
        }, "".concat(plan.displayName, "-").concat(plan.dataType, "-").concat(index));
      })
    })]
  });
};